// @kawai/ui primitive のクラス解決（pure / DOM 非依存）。
//
// 各 admin app に inline 重複していた Tailwind class 文字列を、配色トークン
// （@kawai/ui/tokens.css の CSS 変数）へ正規化して 1 箇所に集約する。
// 旧コードの生パレット（slate-300 / admin-accent / text-white 等）は意味的に
// 等価なトークン（border-input / bg-primary / text-primary-foreground 等）へ寄せる。
// コンポーネント層（Button.tsx 等）はここを呼ぶだけにし、本ファイルを単体テストする。
/**
 * truthy な class 片だけを空白連結する最小ユーティリティ。
 *
 * 注意: これは `tailwind-merge` ではないため Tailwind の競合は解決しない。
 * Tailwind の優先順位は class 属性の並び順ではなく**生成 CSS のソース順**で
 * 決まるため、後ろに置いた `className` で base/variant の配色を確実に上書き
 * できるわけではない。各コンポーネントの `className` は**加算的なレイアウト/
 * 余白用**として渡し、variant の配色等を差し替える用途には使わないこと
 * （差し替えが要るなら variant を増やす）。
 */
export function cn(...parts) {
    return parts.filter(Boolean).join(' ');
}
const BUTTON_BASE = 'inline-flex items-center justify-center gap-1.5 rounded-md font-semibold transition-opacity focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60';
const BUTTON_SIZES = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
};
const BUTTON_VARIANTS = {
    // 旧: bg-admin-accent text-white hover:opacity-90
    primary: 'bg-primary text-primary-foreground hover:opacity-90',
    // 旧: border border-slate-300 bg-white text-slate-700 hover:bg-slate-50（戻る/キャンセル）
    secondary: 'border border-border bg-card text-foreground hover:bg-accent',
    // 旧: border border-admin-accent text-admin-accent hover:bg-admin-accent/5
    outline: 'border border-primary text-primary-strong hover:bg-primary/5',
    destructive: 'bg-destructive text-destructive-foreground hover:opacity-90',
    ghost: 'text-foreground hover:bg-accent',
    // 旧: bg-slate-900 text-white hover:bg-slate-700（サインアウト等の暗い neutral-strong）。
    // ocean パレットに neutral-strong トークンが無いため、status 用の Badge
    // （success/warning の生 emerald/amber）と同じく生 slate を使う（neutral-strong
    // トークン新設までの暫定 / 配色完全一致を優先）。
    dark: 'bg-slate-900 text-white hover:bg-slate-700',
};
const BUTTON_V2_BASE = 'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-opacity focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50';
/** v2 のサイズ。md = 本文 15px / sm = 見出し語 14px（行内の小ボタン）。primary だけ左右 20px。 */
const BUTTON_V2_SIZES = {
    md: { primary: 'px-5 py-2 text-body', other: 'px-4 py-2 text-body' },
    sm: { primary: 'px-4 py-1.5 text-label', other: 'px-3.5 py-1.5 text-label' },
};
const BUTTON_V2_VARIANTS = {
    primary: 'bg-brand text-white hover:opacity-90',
    secondary: 'bg-surface-2 text-ink hover:bg-surface-3',
    paper: 'bg-white text-ink hover:bg-surface-3',
    danger: 'bg-surface-2 text-danger-ink hover:bg-surface-3',
    soft: 'bg-surface-2 text-brand-ink hover:bg-surface-3',
    ghost: 'text-ink-2 hover:bg-surface-2',
};
/** v1 の variant 名を v2 に読み替える（`UiV2Scope` の中で v1 の呼び出しがそのまま動くように）。 */
const BUTTON_V1_TO_V2 = {
    primary: 'primary',
    secondary: 'secondary',
    outline: 'soft',
    destructive: 'danger',
    ghost: 'ghost',
    dark: 'primary',
};
function isV2Variant(v) {
    return v in BUTTON_V2_VARIANTS;
}
export function buttonClasses(options = {}) {
    const { variant = 'primary', size = 'md', fullWidth = false, v2 = false, className } = options;
    if (v2) {
        const v = isV2Variant(variant) ? variant : BUTTON_V1_TO_V2[variant];
        return cn(BUTTON_V2_BASE, BUTTON_V2_SIZES[size][v === 'primary' ? 'primary' : 'other'], BUTTON_V2_VARIANTS[v], fullWidth && 'w-full', className);
    }
    const v1 = variant in BUTTON_VARIANTS ? variant : 'primary';
    return cn(BUTTON_BASE, BUTTON_SIZES[size], BUTTON_VARIANTS[v1], fullWidth && 'w-full', className);
}
// 入力系（Input / Select / Textarea）が共有する枠線・フォーカス・disabled の装飾。
// 旧: w-full rounded-md border border-slate-300 px-3 py-2 text-sm
//     focus:border-admin-accent focus:outline-hidden focus:ring-1 focus:ring-admin-accent
//     disabled:bg-slate-100 disabled:text-slate-400
// block / w-full / padding は size・fullWidth で分離し、テーブルセル等の compact
// 入力（旧 px-2 py-1 + w-24 等）も同じ枠線で表現できるようにする。
const FIELD_FRAME = 'rounded-md border border-input bg-card text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-hidden focus:ring-1 focus:ring-primary disabled:bg-muted disabled:text-muted-foreground';
const FIELD_SIZES = {
    // 旧 compact 入力（テーブルセル / 横並び flex）の px-2 py-1 text-xs に対応。
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-2 text-sm',
};
const FIELD_ERROR = 'border-destructive focus:border-destructive focus:ring-destructive';
const FIELD_V2_FRAME = 'rounded-lg text-ink placeholder:text-ink-3 focus:outline-hidden focus:ring-2 focus:ring-brand disabled:opacity-60';
const FIELD_V2_SURFACES = {
    gray: 'bg-surface-2',
    white: 'bg-white',
};
const FIELD_V2_SIZES = {
    sm: 'px-3 py-1.5 text-label',
    md: 'px-3.5 py-2 text-body',
};
const FIELD_V2_ERROR = 'ring-2 ring-danger-ink focus:ring-danger-ink';
function fieldClasses(options = {}) {
    const { invalid = false, size = 'md', fullWidth = true, v2 = false, surface = 'gray', className, } = options;
    if (v2) {
        return cn(fullWidth && 'block w-full', FIELD_V2_FRAME, FIELD_V2_SURFACES[surface], FIELD_V2_SIZES[size], invalid && FIELD_V2_ERROR, className);
    }
    return cn(fullWidth && 'block w-full', FIELD_FRAME, FIELD_SIZES[size], invalid && FIELD_ERROR, className);
}
export function inputClasses(options = {}) {
    return fieldClasses(options);
}
// Select は Input と同一の枠線/フォーカス/disabled スタイルを共有する。
export function selectClasses(options = {}) {
    return fieldClasses(options);
}
// Textarea も Input と同一の枠線。複数行高さは rows 属性で制御し、preflight の
// `textarea { resize: vertical }` を踏襲する（追加の class は付けない）。
export function textareaClasses(options = {}) {
    return fieldClasses(options);
}
const CARD_PADDINGS = {
    none: '',
    sm: 'p-3',
    md: 'p-5',
    lg: 'p-8',
};
export function cardClasses(options = {}) {
    const { padding = 'md', shadow = 'sm', border = 'line', className } = options;
    return cn('rounded-md bg-card text-card-foreground', border === 'line' && 'border border-border', shadow === 'sm' && 'shadow-xs', CARD_PADDINGS[padding], className);
}
const BADGE_BASE = 'inline-flex items-center rounded-full font-semibold leading-none';
const BADGE_SIZES = {
    sm: 'px-1.5 py-0.5 text-[10px]',
    md: 'px-2 py-0.5 text-xs',
};
const BADGE_TONES = {
    // 旧: bg-slate-100 text-slate-600
    neutral: 'bg-muted text-muted-foreground',
    // 旧: bg-admin-accent/10 text-admin-accent
    primary: 'bg-primary/10 text-primary-strong',
    // success / warning は ocean パレットに対応トークンが無いため status 用の
    // 生パレットを使う（status トークン新設までの暫定 / 旧コードも emerald/amber 直書き）。
    success: 'bg-emerald-100 text-emerald-700',
    warning: 'bg-amber-100 text-amber-700',
    // 旧: bg-red-100 text-red-700
    danger: 'bg-destructive/10 text-destructive',
};
export function badgeClasses(options = {}) {
    const { tone = 'neutral', size = 'md', className } = options;
    return cn(BADGE_BASE, BADGE_SIZES[size], BADGE_TONES[tone], className);
}
const MODAL_DIALOG_SIZES = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
};
export function modalBackdropClasses(className) {
    return cn('fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4', className);
}
export function modalDialogClasses(options = {}) {
    const { size = 'md', v2 = false, className } = options;
    return cn('flex max-h-[85vh] w-full flex-col overflow-hidden', 
    // v2（S-138）: 影なし・角丸 12px・白。黒 40% の背景の上なので面差で足りる
    v2 ? 'rounded-xl bg-white text-ink' : 'rounded-lg bg-card text-card-foreground shadow-xl', MODAL_DIALOG_SIZES[size], className);
}
/** Modal の見出し行 / 本文 / フッター / 閉じるボタン（v1 は罫線区切り、v2 は余白だけで分ける）。 */
export const MODAL_PART_CLASSES = {
    v1: {
        header: 'flex items-center justify-between gap-3 border-b border-border px-5 py-3',
        title: 'text-base font-semibold text-foreground',
        close: 'rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground',
        body: 'flex-1 overflow-y-auto px-5 py-4',
        footer: 'flex items-center justify-end gap-2 border-t border-border px-5 py-3',
    },
    v2: {
        header: 'flex items-center justify-between gap-3 px-[30px] pt-[26px] pb-3',
        title: 'text-h-card font-semibold text-ink',
        close: 'rounded-lg p-1 text-ink-3 hover:bg-surface-2 hover:text-ink',
        body: 'flex-1 overflow-y-auto px-[30px] py-3',
        footer: 'flex items-center justify-end gap-2.5 px-[30px] pt-3 pb-[26px]',
    },
};
const TILE_SURFACES = {
    brand: 'bg-brand-tint',
    success: 'bg-success-tint',
    warning: 'bg-warning-tint',
    neutral: 'bg-surface-2',
    paper: 'bg-white',
};
const TILE_PADDINGS = {
    md: 'gap-1.5 px-[22px] py-5',
    sm: 'gap-1.5 px-5 py-4',
    xs: 'gap-1 px-4 py-3',
};
const TILE_NUM_SIZES = {
    lg: 'text-num-lg',
    md: 'text-num',
    sm: 'text-num-sm',
};
const TILE_VALUE_TONES = {
    ink: 'text-ink',
    brand: 'text-brand-ink',
    success: 'text-success-ink',
    muted: 'text-ink-3',
};
/** タイルの外枠。grid + content-start（無いと値が縦中央に浮く / S-132 の地雷）。 */
export function tileClasses(options = {}) {
    const { variant = 'neutral', padding = 'md', className } = options;
    return cn('grid content-start rounded-[10px]', TILE_PADDINGS[padding], TILE_SURFACES[variant], className);
}
/** タイルの見出し語（14px / 500）。brand 面では brand-ink、他は ink-2。 */
export function tileLabelClasses(variant = 'neutral') {
    return cn('text-label font-medium', variant === 'brand' ? 'text-brand-ink' : 'text-ink-2');
}
/** タイルの数値（ui-num で字間 0.02em / tabular-nums）。tone 省略時は brand 面で brand-ink、他は ink。 */
export function tileValueClasses(options = {}) {
    const { variant = 'neutral', size = 'md', tone } = options;
    const resolvedTone = tone ?? (variant === 'brand' ? 'brand' : 'ink');
    return cn('ui-num font-medium tabular-nums', TILE_NUM_SIZES[size], TILE_VALUE_TONES[resolvedTone]);
}
/** 数値に添える単位（14px / 500 / ink-2）。 */
export const TILE_UNIT_CLASS = 'ml-1 text-label font-medium text-ink-2';
/** タイルの補助文（14px / 400 / ink-3）。 */
export const TILE_SUB_CLASS = 'text-label text-ink-3';
const CHIP_TONES = {
    brand: 'bg-brand-tint text-brand-ink',
    success: 'bg-success-tint text-success-ink',
    warning: 'bg-warning-tint text-warning-ink',
    neutral: 'bg-surface-2 text-ink-2',
};
export function chipClasses(options = {}) {
    const { tone = 'neutral', className } = options;
    return cn('inline-flex items-center rounded-full px-2 py-0.5 text-note font-medium', CHIP_TONES[tone], className);
}
/** ゼブラ表: 縦横線なし。見出し行は bg-surface-2（両端の角丸 8px）、本文は偶数行 bg-surface-2。 */
export function zebraTableClasses(className) {
    return cn('w-full border-collapse text-label', className);
}
export function zebraTableHeadRowClasses(className) {
    return cn('text-left font-medium text-ink-2', className);
}
const ZEBRA_ALIGNS = {
    left: '',
    right: 'text-right',
    center: 'text-center',
};
export function zebraThClasses(options = {}) {
    const { align = 'left', edge, className } = options;
    return cn('bg-surface-2 px-3.5 py-2.5', edge === 'first' && 'rounded-l-lg', edge === 'last' && 'rounded-r-lg', ZEBRA_ALIGNS[align], className);
}
export function zebraRowClasses(className) {
    return cn('even:bg-surface-2', className);
}
const ZEBRA_TD_PADDINGS = {
    md: 'px-3.5 py-2.5',
    lg: 'px-3.5 py-4',
};
export function zebraTdClasses(options = {}) {
    const { numeric = false, align = numeric ? 'right' : 'left', tone = 'ink', padding = 'md', className, } = options;
    return cn(ZEBRA_TD_PADDINGS[padding], ZEBRA_ALIGNS[align], numeric && 'tabular-nums', tone === 'ink' ? 'text-ink' : 'text-ink-3', className);
}
//# sourceMappingURL=styles.js.map